import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { syncRunningApi } from "../../Redux/Tasks/Task.actions";
import { subscribeRunningChanged } from "../../Redux/Tasks/runningChannel";

const POLL_MS = 30000;

// The timer lives on the server and can be stopped from anywhere - another
// tab, the phone - so a tracker left open all day can't trust what it last
// saw. It asks again on mount, whenever the tab comes back to the front, and
// every 30 seconds while it's on screen. A hidden tab polls nothing: browsers
// throttle it to near-uselessness anyway, and it gets resynced the moment
// anyone looks at it. Other tabs of this browser don't wait for the interval,
// they announce their changes over the channel.
const useRunningSync = (orgId, token) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!token || !orgId) return;
    let interval = null;
    let lastSync = 0;

    const sync = () => {
      // visibilitychange and focus both fire when a tab is switched back to,
      // and this is a network call, so the second one gets dropped.
      const now = Date.now();
      if (now - lastSync < 1000) return;
      lastSync = now;
      dispatch(syncRunningApi(orgId, token));
    };

    const stopPolling = () => {
      if (interval) clearInterval(interval);
      interval = null;
    };
    const startPolling = () => {
      stopPolling();
      interval = setInterval(sync, POLL_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState !== "visible") {
        stopPolling();
        return;
      }
      sync();
      startPolling();
    };

    sync();
    if (document.visibilityState === "visible") startPolling();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", sync);
    // A sibling tab saying "it changed" is never a duplicate, so it skips the
    // guard above.
    const unsubscribe = subscribeRunningChanged(() => dispatch(syncRunningApi(orgId, token)));

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", sync);
      unsubscribe();
    };
  }, [orgId, token, dispatch]);
};

export default useRunningSync;
